import { toast } from 'sonner'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
    createAccount,
    fetchAccounts,
    getAccountById,
    listAccountParticipants,
    updateAccountSettings,
    updateTradeConfirmationEmail,
    updateRegistrationAddress,
    upsertAccountParticipants,
    getAccountUsagePolicies,
} from './accountApi'
import { Errors } from '@/common/strings'
import { API_ROUTES } from '@/common/constants'
import { defaultOptions } from '@/common/utils/queryOptions'
import useUserStore from '@/user/userStore'
import { getAccountConflictId } from './accountConflictError'
import type { CreateAccountPayload, UpdateAccountSettingsPayload } from './type'
import type { AddressDetails } from '@/common/types'
import type { UpsertAccountParticipantsRequest } from '@six-invest/api-types'

export const useCreateAccount = () => {
    const queryClient = useQueryClient()
    const { user } = useUserStore((state) => ({ user: state.user }))

    return useMutation({
        mutationFn: (data: CreateAccountPayload) => createAccount(data),
        onSuccess: async () => {
            if (!user?.userId) return

            // Invalidate account queries to refetch fresh data
            await queryClient.invalidateQueries({ queryKey: [API_ROUTES.accounts, user.userId] })
        },
        onError: (err) => {
            if (getAccountConflictId(err)) return
            toast.error(Errors.server)
        },
    })
}

export const useUpsertAccountParticipants = () => {
    return useMutation({
        mutationFn: (data: UpsertAccountParticipantsRequest & { accountId: string }) => {
            const { accountId, ...payload } = data
            return upsertAccountParticipants(accountId, payload)
        },
        onError: () => {
            toast.error(Errors.server)
        },
    })
}

export const useFetchAccountParticipants = (accountId: string, options = defaultOptions) => {
    const { enabled } = options

    return useQuery({
        queryKey: [API_ROUTES.accountsParticipants, accountId],
        queryFn: () => listAccountParticipants(accountId),
        enabled: (enabled ?? true) && Boolean(accountId),
    })
}

export const useFetchAccountDetail = (accountId?: string | null, options = defaultOptions) => {
    const { enabled } = options

    return useQuery({
        queryKey: [API_ROUTES.accountSettings, accountId],
        queryFn: () => getAccountById(accountId!),
        enabled: (enabled ?? true) && Boolean(accountId),
    })
}

export const useFetchAccounts = (userId: string | null, isEnabled = true) => {
    return useQuery({
        queryKey: [API_ROUTES.accounts, userId],
        queryFn: fetchAccounts,
        // Overrides the app-wide refetchOnMount: false default. This query must always
        // re-verify against the server on mount: account creation/switching invalidates
        // this key while AccountProvider may not be mounted to observe it, so a later
        // remount would otherwise silently serve the stale (pre-invalidation) cache.
        refetchOnMount: true,
        enabled: isEnabled && !!userId,
    })
}

export const useUpdateAccountSettings = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: UpdateAccountSettingsPayload & { accountId: string }) => {
            const { accountId, ...payload } = data
            return updateAccountSettings(accountId, payload)
        },
        onSuccess: async (_data, variables) => {
            await queryClient.invalidateQueries({ queryKey: [API_ROUTES.accountSettings, variables.accountId] })
        },
        onError: () => {
            toast.error(Errors.server)
        },
    })
}

export const useUpdateTradeConfirmationEmail = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: { accountId: string; email: string }) => {
            const { accountId, email } = data
            return updateTradeConfirmationEmail(accountId, email)
        },
        onSuccess: async (_data, variables) => {
            await queryClient.invalidateQueries({ queryKey: [API_ROUTES.accountSettings, variables.accountId] })
        },
        onError: () => {
            toast.error(Errors.server)
        },
    })
}

export const useUpdateRegistrationAddress = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: (data: { accountId: string; address: AddressDetails }) => {
            const { accountId, address } = data
            return updateRegistrationAddress(accountId, address)
        },
        onSuccess: async (_data, variables) => {
            await queryClient.invalidateQueries({ queryKey: [API_ROUTES.accountSettings, variables.accountId] })
        },
        onError: () => {
            toast.error(Errors.server)
        },
    })
}

export const useFetchAccountUsagePolicies = (accountId?: string) => {
    return useQuery({
        queryKey: [API_ROUTES.accountUsagePolicies, accountId],
        queryFn: () => getAccountUsagePolicies(accountId!),
        enabled: !!accountId,
    })
}
